import React, { useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import FunderRenderer from '../HelperComponents/FunderRenderer';
import RecipientRenderer from '../HelperComponents/RecipientRenderer';
import Customize from '../../assets/customize.png';

// Add this URL renderer component at the top of the file, after the imports
const WebsiteRenderer = (params) => {
    if (!params.value) return 'N/A';
    
    // Ensure the URL has a protocol
    let url = params.value;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
    }
    
    return (
        <a 
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#4338CA'}}
            onClick={(e) => e.stopPropagation()}
        >
            {params.value}
        </a>
    );
};

function RawAwardsView({ 
    rawAwards    
}) {    
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [filteredRowCount, setFilteredRowCount] = useState(rawAwards.length);
    const [selectedColumns, setSelectedColumns] = useState(new Set([
        'funder_name', 'recipient_name', 
        'cash_grant_amt', 'purpose', 'form_990.website_address_txt', 'form_990.phone_number', 'state_abbreviation_cd', 'city_name', 
    ]));

    // All available column definitions
    const allColumnDefs = [
        { 
            headerName: "Tax Year", 
            field: "tax_year",
            width: 100
        },
        { 
            headerName: "Funder Name", 
            field: "funder_name",
            cellRenderer: FunderRenderer
        },
        { 
            headerName: "Funder EIN", 
            field: "funder_ein",
            width: 120
        },
        { 
            headerName: "Recipient Name", 
            field: "recipient_name",
            filter: "agTextColumnFilter",
            cellRenderer: RecipientRenderer
        },
        { 
            headerName: "Recipient EIN", 
            field: "recipient_ein",
            width: 120
        },
        { 
            headerName: "Amount", 
            field: "cash_grant_amt", 
            valueGetter: ({ data }) => data.cash_grant_amt ? parseFloat(data.cash_grant_amt) : 0,
            valueFormatter: ({ value }) => `$${value.toLocaleString()}`,
            filter: "agNumberColumnFilter",
            width: 100
        },
        { 
            headerName: "Purpose", 
            field: "purpose",
            filter: "agTextColumnFilter"
        },
        { 
            headerName: "Funder Website", 
            field: "form_990.website_address_txt",
            width: 150,
            cellRenderer: WebsiteRenderer
        },
        { 
            headerName: "Funder Phone", 
            field: "form_990.phone_number",
            width: 120,
            valueFormatter: ({ value }) => {
                if (!value) return 'N/A';
                // Remove all non-digits
                const digits = value.replace(/\D/g, '');
                // Format as (XXX) XXX-XXXX
                if (digits.length === 10) {
                    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                }
                // Return original if not 10 digits
                return value;
            }
        },
        { 
            headerName: "Address", 
            field: "address_line_1_text",
            filter: "agTextColumnFilter"
        },
        { 
            headerName: "Recipient City", 
            field: "city_name",
            filter: "agTextColumnFilter"
        },
        { 
            headerName: "Recipient State", 
            field: "state_abbreviation_cd",
            filter: "agTextColumnFilter"
        },
        { 
            headerName: "Recipient ZIP", 
            field: "zip_code",
            filter: "agTextColumnFilter"
        },
        { 
            headerName: "NTEE Full", 
            field: "ntee_codes.ntee_full",
            width: 100
        },
        { 
            headerName: "NTEE Major", 
            field: "ntee_codes.ntee_major",
            width: 80
        },
        { 
            headerName: "NTEE Category", 
            field: "ntee_codes.ntee_category",
            width: 100
        },
        { 
            headerName: "Form 990 Business Name", 
            field: "form_990.business_name",
            filter: "agTextColumnFilter"
        },
        { 
            headerName: "Tax Period End", 
            field: "form_990.tax_period_end_dt",
            width: 120
        },
        { 
            headerName: "Gross Receipts", 
            field: "form_990.gross_receipts_amt",
            valueGetter: ({ data }) => data.form_990?.gross_receipts_amt ? parseFloat(data.form_990.gross_receipts_amt) : 0,
            valueFormatter: ({ value }) => `$${value.toLocaleString()}`,
            filter: "agNumberColumnFilter"
        },
        { 
            headerName: "Total Assets (EOY)", 
            field: "form_990.total_assets_eoy_amt",
            valueGetter: ({ data }) => data.form_990?.total_assets_eoy_amt ? parseFloat(data.form_990.total_assets_eoy_amt) : 0,
            valueFormatter: ({ value }) => `$${value.toLocaleString()}`,
            filter: "agNumberColumnFilter"
        },
        { 
            headerName: "Mission Description", 
            field: "form_990.mission_desc",
            filter: "agTextColumnFilter"
        },

        { 
            headerName: "Formation Year", 
            field: "form_990.formation_yr",
            width: 100
        },
        { 
            headerName: "Legal State", 
            field: "form_990.legal_domicile_state_cd",
            width: 100
        },
        { 
            headerName: "Created At", 
            field: "created_at",
            width: 150
        }
    ];

    // Filter column definitions based on selected columns
    const awardsReceivedColumnDefs = allColumnDefs.filter(col => 
        selectedColumns.has(col.field)
    );

    const handleColumnToggle = (field) => {
        const newSelectedColumns = new Set(selectedColumns);
        if (newSelectedColumns.has(field)) {
            newSelectedColumns.delete(field);
        } else {
            newSelectedColumns.add(field);
        }
        setSelectedColumns(newSelectedColumns);
    };

    // Handle filter changes
    const onFilterChanged = (params) => {
        const filteredRows = params.api.getDisplayedRowCount();
        setFilteredRowCount(filteredRows);
    };

    const ColumnSelectionModal = () => (
        <div 
            className="modal-overlay" 
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}
            onClick={() => setShowColumnModal(false)}
        >
            <div 
                className="modal p-4 sm:p-6 lg:p-8 overflow-auto max-h-full mx-4 sm:mx-0" 
                style={{ 
                    cursor: 'default', 
                    width: '95vw',
                    maxWidth: '500px',
                    minHeight: 'auto',
                    maxHeight: '90vh',
                    backgroundColor: 'white', 
                    borderRadius: '10px'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Select Columns</div>
                <p className="text-sm text-gray-600 mb-3 sm:mb-4">Choose which columns to display in the table:</p>
                
                <div className="space-y-2 max-h-64 sm:max-h-96 overflow-y-auto">
                    {allColumnDefs.map((col) => (
                        <label key={col.field} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 sm:p-3 rounded">
                            <input
                                type="checkbox"
                                checked={selectedColumns.has(col.field)}
                                onChange={() => handleColumnToggle(col.field)}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4 sm:w-5 sm:h-5"
                            />
                            <span className="text-sm sm:text-base break-words">{col.headerName}</span>
                        </label>
                    ))}
                </div>

                <div className="flex justify-end space-x-3 mt-4 sm:mt-6">
                    <button 
                        className="px-3 py-2 sm:px-4 sm:py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm sm:text-base"
                        onClick={() => setShowColumnModal(false)}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className='py-4'>
            {rawAwards.length > 0 ? (
                <>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-md">{filteredRowCount} <span className="text-gray-500">of </span> {rawAwards.length} <span className="text-gray-500"> awards shown</span></h3>
                        <button 
                            onClick={() => setShowColumnModal(true)}
                            className="px-4 py-2 border border-gray-500 bg-gray-200 text-gray-500 rounded hover:bg-gray-300 text-sm"
                        >
                            <img src={Customize} alt="Customize Columns" className='w-4 h-4' />
                        </button>
                    </div>
                    <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
                        <AgGridReact
                            rowData={rawAwards}
                            columnDefs={awardsReceivedColumnDefs}
                            onFilterChanged={onFilterChanged}
                            defaultColDef={{
                                cellStyle: { fontSize: '0.625rem' },
                                headerClass: 'text-xs',
                                minWidth: 60,
                                maxWidth: 200
                            }}
                            components={{
                                agColumnHeader: (props) => (
                                    <div className="text-xs font-medium">
                                        {props.displayName}
                                    </div>
                                )
                            }}
                        />
                    </div>
                </>
            ) : null}
            
            {showColumnModal && <ColumnSelectionModal />}
        </div>
    );
}

export default RawAwardsView;